import { Link } from "react-router-dom";
import { Heart, X, Star, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

export default function VillaCard({ villa, onAction }) {
  const { user, refresh } = useAuth();
  const [saved, setSaved] = useState(user?.saved_villas?.includes(villa.id) || false);
  const [notInt, setNotInt] = useState(user?.not_interested?.includes(villa.id) || false);
  const [imgIndex, setImgIndex] = useState(0);

  const images = villa.images?.length ? villa.images : [null];
  const discounted = villa.discount_percent ? Math.round(villa.price_per_night * (1 - villa.discount_percent / 100)) : villa.price_per_night;
  const isGuestFavorite = villa.rating >= 4.8;

  const handleSave = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast("Please sign in to save villas"); return; }
    try {
      const { data } = await api.post(`/villas/${villa.id}/save`);
      setSaved(data.saved);
      if (data.saved) setNotInt(false);
      toast(data.saved ? "Saved to your collection" : "Removed from saved");
      refresh();
      onAction?.();
    } catch { toast("Could not save"); }
  };
  const handleNotInt = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast("Please sign in"); return; }
    try {
      const { data } = await api.post(`/villas/${villa.id}/not-interested`);
      setNotInt(data.not_interested);
      if (data.not_interested) setSaved(false);
      toast(data.not_interested ? "Hidden from feed" : "Restored");
      refresh();
      onAction?.();
    } catch { /* noop */ }
  };
  const handleShare = (e) => {
    e.preventDefault(); e.stopPropagation();
    const url = `${window.location.origin}/villas/${villa.id}`;
    navigator.clipboard?.writeText(url);
    toast("Link copied to clipboard");
  };
  const goto = (dir) => (e) => {
    e.preventDefault(); e.stopPropagation();
    setImgIndex((i) => (i + dir + images.length) % images.length);
  };

  return (
    <Box component={Link} to={`/villas/${villa.id}`} className="villa-card" data-testid={`villa-card-${villa.id}`} sx={{ display: "block", textDecoration: "none", color: "inherit" }}>
      <Box sx={{ position: "relative", overflow: "hidden", borderRadius: 3.5, aspectRatio: "1 / 1", bgcolor: "action.hover" }}>
        <Box
          component="img"
          src={images[imgIndex]}
          alt={villa.name}
          loading="lazy"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s" }}
        />

        {isGuestFavorite && (
          <Box
            sx={{
              position: "absolute", top: 12, left: 12, bgcolor: "background.paper", color: "text.primary",
              fontSize: 11, fontWeight: 600, px: 1.25, py: 0.6, borderRadius: 999, boxShadow: 2,
            }}
          >
            Guest favourite
          </Box>
        )}
        {villa.discount_percent > 0 && (
          <Box
            className="bg-gold"
            sx={{ position: "absolute", top: isGuestFavorite ? 44 : 12, left: 12, fontSize: 10, fontWeight: 600, color: "#fff", px: 1.25, py: 0.5, borderRadius: 999 }}
          >
            {villa.discount_percent}% off
          </Box>
        )}

        <IconButton
          onClick={handleSave}
          data-testid={`save-btn-${villa.id}`}
          aria-label="Save"
          size="small"
          sx={{
            position: "absolute", top: 10, right: 10, bgcolor: "rgba(0,0,0,0.35)", color: saved ? "primary.main" : "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
          }}
        >
          <Heart size={16} fill={saved ? "currentColor" : "none"} />
        </IconButton>

        {images.length > 1 && (
          <>
            <IconButton onClick={goto(-1)} size="small" aria-label="Previous photo" sx={{
              position: "absolute", top: "50%", left: 8, transform: "translateY(-50%)", bgcolor: "#fff", boxShadow: 2,
              opacity: 0, ".villa-card:hover &": { opacity: 1 }, width: 28, height: 28,
            }}>
              <ChevronLeft size={14} />
            </IconButton>
            <IconButton onClick={goto(1)} size="small" aria-label="Next photo" sx={{
              position: "absolute", top: "50%", right: 8, transform: "translateY(-50%)", bgcolor: "#fff", boxShadow: 2,
              opacity: 0, ".villa-card:hover &": { opacity: 1 }, width: 28, height: 28,
            }}>
              <ChevronRight size={14} />
            </IconButton>
            <Box sx={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 0.5 }}>
              {images.map((_, i) => (
                <Box key={i} sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: i === imgIndex ? "#fff" : "rgba(255,255,255,0.55)" }} />
              ))}
            </Box>
          </>
        )}

        <Box sx={{ position: "absolute", bottom: 10, right: 12, display: "flex", gap: 0.5 }}>
          <IconButton onClick={handleNotInt} data-testid={`hide-btn-${villa.id}`} aria-label="Not interested" size="small" sx={{ bgcolor: "rgba(0,0,0,0.35)", color: notInt ? "primary.main" : "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.5)" } }}>
            <X size={13} />
          </IconButton>
          <IconButton onClick={handleShare} data-testid={`share-btn-${villa.id}`} aria-label="Share" size="small" sx={{ bgcolor: "rgba(0,0,0,0.35)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.5)" } }}>
            <Share2 size={12} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ pt: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {villa.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexShrink: 0 }}>
            <Star size={13} fill="currentColor" />
            <Typography sx={{ fontSize: 13 }}>{villa.rating}</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.25 }}>{villa.location}</Typography>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{villa.reviews_count} reviews</Typography>
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "baseline", gap: 0.5 }}>
          {villa.discount_percent > 0 && (
            <Typography sx={{ fontSize: 13, color: "text.secondary", textDecoration: "line-through" }}>
              ₹{villa.price_per_night.toLocaleString()}
            </Typography>
          )}
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>₹{discounted.toLocaleString()}</Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>night</Typography>
        </Box>
      </Box>
    </Box>
  );
}
